import { useCallback, useState } from "react";
import type { InviteUserFormData } from "../components/InviteUserForm/InviteUserForm";
import { useAccessSelection } from "./useAccessSelection";

export type UseInviteUserFormStateOptions = {
  onSubmit: (data: InviteUserFormData) => void;
};

export type UseInviteUserFormStateResult = {
  email: string;
  emailError: string | undefined;
  permissionsError: string | undefined;
  tenantAdmin: boolean;
  siteSelection: Map<string, "manager" | "viewer">;
  cameraSelection: Map<string, "manager" | "viewer">;
  handleEmailChange: (text: string) => void;
  handleTenantAdminToggle: () => void;
  handleSiteToggle: (id: string) => void;
  handleSiteRoleChange: (id: string, role: "manager" | "viewer") => void;
  handleCameraToggle: (id: string) => void;
  handleCameraRoleChange: (id: string, role: "manager" | "viewer") => void;
  handleSubmit: () => void;
};

const EMPTY_PERMISSIONS: [] = [];

/**
 * Manages the invite-user form state including validation.
 * The invite form starts with no selections — useAccessSelection with empty permissions
 * provides the toggle/role-change mechanics.
 */
export function useInviteUserFormState({ onSubmit }: UseInviteUserFormStateOptions): UseInviteUserFormStateResult {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [permissionsError, setPermissionsError] = useState<string | undefined>(undefined);
  const [tenantAdmin, setTenantAdmin] = useState(false);

  const {
    selected: siteSelection,
    handleToggle: handleSiteToggleBase,
    handleRoleChange: handleSiteRoleChange,
  } = useAccessSelection({ permissions: EMPTY_PERMISSIONS });

  const {
    selected: cameraSelection,
    handleToggle: handleCameraToggleBase,
    handleRoleChange: handleCameraRoleChange,
  } = useAccessSelection({ permissions: EMPTY_PERMISSIONS });

  const handleEmailChange = useCallback(
    (text: string) => {
      setEmail(text);
      if (emailError) setEmailError(undefined);
    },
    [emailError],
  );

  const handleTenantAdminToggle = useCallback(() => {
    setTenantAdmin((prev) => !prev);
    setPermissionsError(undefined);
  }, []);

  const handleSiteToggle = useCallback(
    (id: string) => {
      handleSiteToggleBase(id);
      setPermissionsError(undefined);
    },
    [handleSiteToggleBase],
  );

  const handleCameraToggle = useCallback(
    (id: string) => {
      handleCameraToggleBase(id);
      setPermissionsError(undefined);
    },
    [handleCameraToggleBase],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return;
    }
    if (!trimmed.includes("@") || !trimmed.includes(".")) {
      setEmailError("Enter a valid email address");
      return;
    }

    if (!tenantAdmin && siteSelection.size === 0 && cameraSelection.size === 0) {
      setPermissionsError("Select at least one permission to assign");
      return;
    }

    onSubmit({
      email: trimmed,
      tenantAdmin,
      sites: siteSelection,
      cameras: cameraSelection,
    });
  }, [onSubmit, email, tenantAdmin, siteSelection, cameraSelection]);

  return {
    email,
    emailError,
    permissionsError,
    tenantAdmin,
    siteSelection,
    cameraSelection,
    handleEmailChange,
    handleTenantAdminToggle,
    handleSiteToggle,
    handleSiteRoleChange,
    handleCameraToggle,
    handleCameraRoleChange,
    handleSubmit,
  };
}
